/**
 * Shared date/time display formatting.
 *
 * The portal has one date convention and it is not negotiable per page: dates read as
 * **DD-Mmm-YY** (e.g. 08-Jul-26), never purely numeric, so "08-07-26" can never be read as
 * the 7th of August. Where a moment rather than a day is being shown, the clock time is
 * appended.
 *
 * This module exists because that rule had started being re-implemented per area —
 * `reports2/report-formatting.js`, `my-profile/profile-formatting.js` and (before this)
 * a third copy for system messages. Both of those now delegate here and keep their own
 * exported names, so the rule lives in exactly one place while call sites stay put.
 * Area modules should re-export or wrap these rather than copying the month table again.
 *
 * Every formatter degrades to an em-dash rather than throwing or rendering "Invalid Date":
 * a missing date on a page is a gap, not a crash.
 */

export const MONTH_ABBREVIATIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


const EM_DASH = "—";




/**
 * Coerce a Date | ms | ISO string into a usable Date, or null.
 *
 * A .NET default/unset DateTime ("0001-01-01...", year 1) counts as unusable: it is what
 * an unpopulated column deserialises to, and it would otherwise render as the
 * broken-looking "01-Jan-" (year 1 has no meaningful last two digits).
 */
function toUsableDate(value)
{
	if (!value) return null;

	const date = value instanceof Date ? value : new Date(value);

	if (isNaN(date.getTime())) return null;
	if (date.getFullYear() <= 1) return null;

	return date;
}




/**
 * A date as DD-Mmm-YY (e.g. 08-Jul-26). Em-dash when there isn't one.
 */
export function formatDisplayDate(value)
{
	const date = toUsableDate(value);
	if (date == null) return EM_DASH;

	return String(date.getDate()).padStart(2, "0")
		+ "-" + MONTH_ABBREVIATIONS[date.getMonth()]
		+ "-" + String(date.getFullYear()).slice(2);
}




/**
 * A clock time as h:mm AM/PM (e.g. "8:42 AM"), in the reader's own timezone.
 */
export function formatDisplayTime(value)
{
	const date = toUsableDate(value);
	if (date == null) return EM_DASH;

	const hours24 = date.getHours();
	const meridiem = hours24 < 12 ? "AM" : "PM";

	let hours12 = hours24 % 12;
	if (hours12 === 0) hours12 = 12;

	const minutes = String(date.getMinutes()).padStart(2, "0");

	return hours12 + ":" + minutes + " " + meridiem;
}




/**
 * A moment as DD-Mmm-YY, h:mm AM/PM (e.g. "21-Jul-26, 8:42 AM"), in the reader's own
 * timezone. Use this wherever the time of day matters — a maintenance window, a sign-in,
 * an approval — and `formatDisplayDate` where only the day does.
 */
export function formatDisplayMoment(value)
{
	const date = toUsableDate(value);
	if (date == null) return EM_DASH;

	return formatDisplayDate(date) + ", " + formatDisplayTime(date);
}




/**
 * An ISO date string (yyyy-mm-dd) for an API request, built from the reader's local
 * calendar date rather than via toISOString() — which converts to UTC first and so rolls
 * the date over for anyone east or west of Greenwich late in the day.
 */
export function toIsoDate(value)
{
	const date = toUsableDate(value);
	if (date == null) return "";

	return date.getFullYear()
		+ "-" + String(date.getMonth() + 1).padStart(2, "0")
		+ "-" + String(date.getDate()).padStart(2, "0");
}




/**
 * A value for an `<input type="datetime-local">`, which needs exactly
 * "yyyy-MM-ddTHH:mm" and interprets it in the reader's own timezone.
 *
 * Pass a UTC instant (an ISO string carrying "Z") and the reader edits it in their local
 * time, which is the only timezone they can reason about.
 */
export function toDateTimeLocalInputValue(value)
{
	const date = toUsableDate(value);
	if (date == null) return "";

	return toIsoDate(date)
		+ "T" + String(date.getHours()).padStart(2, "0")
		+ ":" + String(date.getMinutes()).padStart(2, "0");
}




/**
 * The reverse of {@link toDateTimeLocalInputValue}: what an
 * `<input type="datetime-local">` holds, as a UTC ISO string for the API.
 *
 * The input's value has no timezone on it and means "local", so it is parsed as local and
 * converted — never sent as-is, which would have the server read a local wall-clock time
 * as UTC.
 */
export function dateTimeLocalInputValueToUtcIso(value)
{
	if (!value) return "";

	const date = new Date(value);
	if (isNaN(date.getTime())) return "";

	return date.toISOString();
}
